import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ButtonsDivFlush,
  CloseDiv,
  CloseImg,
  CreateButtonImage,
  ErrorMessage,
  HeaderDiv,
  SkillChip,
  SkillChipRemoveButton,
  SkillChipsWrap,
  TipText,
} from '../CreateAccessUserModal/style'
import {
  ActionOutlineButton,
  ActivateButtonDiv,
  BadgeItem,
  CalloutBox,
  CompositionBox,
  CompositionLabel,
  CompositionRow,
  CompositionValue,
  Container,
  CreateButtonDiv,
  CreateButtonText,
  DangerButtonDiv,
  DangerOutlineButton,
  FooterContainer,
  Grid2Cols,
  Grid4Cols,
  HeaderText,
  InputDiv,
  InputField,
  LabelCreate,
  ModalActionsColumn,
  ModalBodyContent,
  PrimaryButton,
  RichHeaderContainer,
  RightFooterActions,
  SecondaryButton,
  SectionTitle,
  SelectField,
  TabNavItem,
  TabsNavContainer,
  UserDetailsColumn,
  UserInfoGroup,
  UserMetaInfo,
  UserNameTitle,
  UserNameTitleRow,
  WarningText,
} from './style'
import { FullUserData } from '../../types/userDetails'
import {
  UserStatus,
  type UpdateUserStatusDto,
} from '../../types/userStatus'
import ConfirmationModal from '../ModalConfirmation'
import cross from '../../assets/addStockModal/cross.svg'
import ModalContext, { SkillOption } from '../../contexts/ModalContext'
import GlobalUserAvatar from '../UserAvatar'
import { useProfileContext } from '../../contexts/AccessesContext'
import { Info, Save } from 'lucide-react'
import axios from '../../config/axios'
import ApiService from '../../services/ApiService'
import AttachmentApiService from '../../services/AttachmentApiService'
import UserAttachmentSection, { type AttachmentData } from '../UserAttachmentSection'
import UserDocumentsTab from '../UserDocumentsTab'
import InfiniteSelect from '../InfiniteSelect'
import { formatCep, formatCnpj, formatCpf, formatDate, formatMonthYear, formatPhone, formatPis, formatRg } from '../../utils/masks'
import { toAccessUserForEdit } from '../../pages/ClientPanel/Accesses/AccessesTable'

/** Corpo esperado pelo `PUT /backend/user/:id` (UpdateUserDto) */
export type UpdateUserDto = {
  email: string
  phone: string
  profileId: number
  name: string
}

export function buildUpdateUserDto(form: {
  name: string
  email: string
  phone: string
  profileId: string
}): UpdateUserDto {
  return {
    name: form.name.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    profileId: form.profileId ? Number(form.profileId) : 0,
  }
}

export async function updateAccessUser(
  userId: number,
  userData: Record<string, any>,
  attachments: AttachmentData[],
): Promise<any> {
  const uploadedAttachments = await Promise.all(
    attachments.map(async (a) => {
      let generalFileId = a.id
      if (a.file) {
        const uploaded = await AttachmentApiService.upload(a.file, a.title)
        generalFileId = uploaded.id
      }
      return {
        id: generalFileId,
        generalFileId,
        title: a.title,
        documentType: (a as any).documentType || 'GERAL',
        expirationDate: (a as any).expirationDate || null,
        status: (a as any).status || null,
      }
    }),
  )

  const formData = new FormData()
  Object.entries(userData).forEach(([k, v]) => {
    if (v === null || v === undefined) {
      formData.append(k, '')
    } else if (Array.isArray(v) || typeof v === 'object') {
      formData.append(k, JSON.stringify(v))
    } else {
      formData.append(k, String(v))
    }
  })
  formData.append('filesMetadata', JSON.stringify(uploadedAttachments))

  const response = await axios.put(
    `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/${userId}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response.data
}

export async function updateAccessUserStatus(
  userId: number,
  dto: UpdateUserStatusDto,
): Promise<void> {
  await ApiService.user.updateStatus(userId, dto)
}

export async function deactivateAccessUser(userId: number): Promise<void> {
  return updateAccessUserStatus(userId, { status: UserStatus.INACTIVE })
}

export async function activateAccessUser(userId: number): Promise<void> {
  return updateAccessUserStatus(userId, { status: UserStatus.ACTIVE })
}

const EditAccessUserModal: React.FC = () => {
  const {
    setModalActive,
    selectedAccessUser,
    setSelectedAccessUser,
    refreshCallback,
  } = useContext(ModalContext)
  const { profiles } = useProfileContext()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const shouldOpenDocumentsTab =
    searchParams.get('from') === 'attachments' ||
    searchParams.get('tab') === 'documentos' ||
    Boolean(searchParams.get('attachmentId'))

  const [activeTab, setActiveTab] = useState<'dados' | 'vinculos' | 'documentos' | 'atividade'>('dados')
  const [formData, setFormData] = useState<FullUserData & { profileId?: string }>({
    name: '',
    email: '',
    phone: '',
    cpf: '',
    rg: '',
    cnpj: '',
    regime: 'CLT',
    birthDate: '',
    maritalStatus: '',
    pis: '',
    ctps: '',
    ctpsSerie: '',
    ctpsUf: 'SP',
    admissionDate: '',
    esocialRegistry: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    kinship: '',
    street: '',
    number: '',
    neighborhood: '',
    cityUf: '',
    cep: '',
    pixKey: '',
    bank: '',
    agency: '',
    account: '',
    dependentsInfo: '',
    alimony: '',
    tshirtSize: '',
    shirtSize: '',
    pantsSize: '',
    bootSize: '',
    vacationStartMonthYear: '',
    vacationEndMonthYear: '',
    vacationPayDate: '',
    annualAdjustmentRule: 'Março (Vestuário SP) · último: 03/2026 (+5,2%)',
    businessUnit: 'Dustre B2B',
    costCenter: 'Comercial',
    baseSalary: '— sem cargo vinculado',
    bonus: '—',
    commission: 'Carteira padrão (3%)',
    additionals: '—',
    benefits: '—',
    status: 'EM RISCO',
    lastLogin: '12/06',
    profileId: '',
  })
  const [attachments, setAttachments] = useState<AttachmentData[]>([])
  const [selectedSkills, setSelectedSkills] = useState<SkillOption[]>([])
  const [skillInputKey, setSkillInputKey] = useState(0)
  const [error, setError] = useState('')
  const [click, setClick] = useState(false)
  const [statusConfirmAction, setStatusConfirmAction] = useState<
    'activate' | 'deactivate' | null
  >(null)

  useEffect(() => {
    if (!selectedAccessUser) return
    const userAny = selectedAccessUser as any

    const formatDateForInput = (val?: string | Date | null) => {
      if (!val) return ''
      const str = String(val)
      if (str.includes('T')) {
        const datePart = str.split('T')[0]
        const [y, m, d] = datePart.split('-')
        if (y && m && d) return `${d}/${m}/${y}`
      }
      return str
    }

    setFormData({
      name: selectedAccessUser.name || '',
      email: selectedAccessUser.email || '',
      phone: selectedAccessUser.phone || '',
      cpf: userAny.cpf || '',
      rg: userAny.rg || '',
      cnpj: userAny.cnpj || '',
      regime: userAny.regime || 'CLT',
      birthDate: formatDateForInput(userAny.birthDate),
      maritalStatus: userAny.maritalStatus || '',
      pis: userAny.pis || '',
      ctps: userAny.ctps || '',
      ctpsSerie: userAny.ctpsSerie || '',
      ctpsUf: userAny.ctpsUf || userAny.ctpsUF || 'SP',
      admissionDate: formatDateForInput(userAny.admissionDate),
      esocialRegistry: userAny.esocialRegistry || '',
      emergencyContactName: userAny.emergencyContactName || '',
      emergencyContactPhone: userAny.emergencyContactPhone || '',
      kinship: userAny.kinship || '',
      street: userAny.street || '',
      number: userAny.number || '',
      neighborhood: userAny.neighborhood || '',
      cityUf: userAny.cityUf || '',
      cep: userAny.cep || '',
      pixKey: userAny.pixKey || '',
      bank: userAny.bank || '',
      agency: userAny.agency || '',
      account: userAny.account || '',
      dependentsInfo: userAny.dependentsInfo || '',
      alimony: userAny.alimony || '',
      tshirtSize: userAny.tshirtSize || '',
      shirtSize: userAny.shirtSize || '',
      pantsSize: userAny.pantsSize || '',
      bootSize: userAny.bootSize || '',
      vacationStartMonthYear: userAny.vacationStartMonthYear || '',
      vacationEndMonthYear: userAny.vacationEndMonthYear || '',
      vacationPayDate: formatDateForInput(userAny.vacationPayDate),
      annualAdjustmentRule: userAny.annualAdjustmentRule || userAny.annualAdjusmentRule || '',
      businessUnit: userAny.businessUnit || 'Dustre B2B',
      costCenter: userAny.costCenter || 'Comercial',
      baseSalary: userAny.baseSalary || '— sem cargo vinculado',
      bonus: userAny.bonus || '—',
      commission: userAny.commission || 'Carteira padrão (3%)',
      additionals: userAny.additionals || '—',
      benefits: userAny.benefits || '—',
      status: userAny.accessStatus || selectedAccessUser.status || 'PENDING',
      lastLogin: userAny.lastLogin || 'Primeiro acesso',
      profileId:
        selectedAccessUser.profileId != null
          ? String(selectedAccessUser.profileId)
          : '',
    })
    const userFiles = (selectedAccessUser as any).FileInUser?.length
      ? (selectedAccessUser as any).FileInUser
      : selectedAccessUser.attachments || []

    setAttachments(
      userFiles.map((a: any) => {
        let expDateStr = a.expirationDate || ''
        if (expDateStr && String(expDateStr).includes('T')) {
          const [datePart] = String(expDateStr).split('T')
          const [y, m, d] = datePart.split('-')
          if (y && m && d) expDateStr = `${d}/${m}/${y}`
        }

        return {
          id: a.GeneralFile?.id || a.fileId || a.id,
          fileUrl: a.GeneralFile?.fileUrl || a.fileUrl,
          title: a.GeneralFile?.title || a.title || 'Documento',
          documentType: a.documentType || 'GERAL',
          expirationDate: expDateStr,
          status: a.status || 'ENTREGUE',
          isEditing: false,
        }
      }),
    )
    setSelectedSkills(selectedAccessUser.skills ?? [])
    setError('')
    setClick(false)
    setStatusConfirmAction(null)
    setActiveTab(shouldOpenDocumentsTab ? 'documentos' : 'dados')
  }, [selectedAccessUser, shouldOpenDocumentsTab])

  const handleCloseModal = () => {
    setModalActive(false)
    if (returnTo) {
      navigate(returnTo, { replace: true })
    }
  }

  const validateForm = () => {
    if (!formData.name || formData.name.length < 3) return false
    if (!formData.email || !formData.email.includes('@')) return false
    if (formData.phone && formData.phone.trim() && formData.phone.trim().length < 10) return false
    return true
  }

  const handleInputChange = (field: string, value: string) => {
    let formattedValue = value
    if (field === 'cpf') formattedValue = formatCpf(value)
    else if (field === 'cnpj') formattedValue = formatCnpj(value)
    else if (field === 'rg') formattedValue = formatRg(value)
    else if (field === 'phone' || field === 'emergencyContactPhone') formattedValue = formatPhone(value)
    else if (field === 'cep') formattedValue = formatCep(value)
    else if (field === 'pis') formattedValue = formatPis(value)
    else if (field === 'birthDate' || field === 'admissionDate' || field === 'vacationPayDate') formattedValue = formatDate(value)
    else if (field === 'vacationStartMonthYear' || field === 'vacationEndMonthYear') formattedValue = formatMonthYear(value)

    setFormData((prev) => ({ ...prev, [field]: formattedValue }))
    setError('')
  }

  const handleSave = async () => {
    if (!selectedAccessUser) return
    if (selectedAccessUser.status === UserStatus.INACTIVE) return
    if (!validateForm()) {
      setClick(true)
      return
    }

   

    try {
      const updatedUser = await updateAccessUser(selectedAccessUser.id, formData, attachments)
      await ApiService.skill.updateUserSkills(
        selectedAccessUser.id,
        selectedSkills.map((s) => s.id),
      )
      if (updatedUser) {
        setSelectedAccessUser(toAccessUserForEdit(updatedUser))
      }
      refreshCallback?.()
      setModalActive(false)
      if (returnTo) {
        navigate(returnTo, { replace: true })
      }
    } catch (error: unknown) {
      console.error('[EditAccessUserModal] Falha ao salvar', error)
      const err = error as { response?: { status?: number; data?: { message?: string | string[] } } }
      if (err.response?.status === 400) {
        const messages = err.response?.data?.message
        if (Array.isArray(messages)) {
          setError(messages.join(', '))
        } else if (typeof messages === 'string') {
          setError(messages)
        } else {
          setError('Dados inválidos. Verifique os campos.')
        }
      } else {
        setError('Não foi possível salvar. Tente novamente.')
      }
    }
  }

  const handleConfirmStatusChange = async () => {
    if (!selectedAccessUser || !statusConfirmAction) return
    try {
      if (statusConfirmAction === 'deactivate') {
        await deactivateAccessUser(selectedAccessUser.id)
      } else {
        await activateAccessUser(selectedAccessUser.id)
      }
      setStatusConfirmAction(null)
      refreshCallback?.()
      setModalActive(false)
      if (returnTo) {
        navigate(returnTo, { replace: true })
      }
    } catch (e) {
      console.error('[EditAccessUserModal] Falha ao alterar status do usuário', e)
      setError(
        statusConfirmAction === 'deactivate'
          ? 'Não foi possível desativar o usuário. Tente novamente.'
          : 'Não foi possível ativar o usuário. Tente novamente.',
      )
      setStatusConfirmAction(null)
    }
  }

  const getInitials = (name: string) => {
    if (!name) return 'US'
    const parts = name.trim().split(' ')
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }

  const isValid = validateForm()

  if (!selectedAccessUser) {
    return null
  }

  const isInactive = selectedAccessUser.status === UserStatus.INACTIVE

  return (
    <Container>
      {/* Cabeçalho Rico com Avatar, Badges e Fechar */}
      <RichHeaderContainer>
        <UserInfoGroup>
          <GlobalUserAvatar
            src={(selectedAccessUser as any)?.picture}
            name={formData.name}
            size={37}
            fontSize="0.95rem"
          />
          <UserDetailsColumn>
            <UserNameTitleRow>
              <UserNameTitle>{formData.name || 'Usuário'}</UserNameTitle>
              {String(selectedAccessUser.status) === 'EM RISCO' && (
                <BadgeItem variant="danger">EM RISCO</BadgeItem>
              )}
            </UserNameTitleRow>
            <UserMetaInfo>
              {formData.email || 'sem e-mail'} {formData.phone ? `· ${formData.phone}` : ''}
            </UserMetaInfo>
          </UserDetailsColumn>
        </UserInfoGroup>

        <CloseDiv onClick={handleCloseModal}>
          <CloseImg src={cross} alt="Fechar" />
        </CloseDiv>
      </RichHeaderContainer>

      {/* Navegação por Abas */}
      <TabsNavContainer>
        <TabNavItem
          type="button"
          isActive={activeTab === 'dados'}
          onClick={() => setActiveTab('dados')}
        >
          Dados
        </TabNavItem>
        <TabNavItem
          type="button"
          isActive={activeTab === 'vinculos'}
          onClick={() => setActiveTab('vinculos')}
        >
          Vínculos e regras
        </TabNavItem>
        <TabNavItem
          type="button"
          isActive={activeTab === 'documentos'}
          onClick={() => setActiveTab('documentos')}
        >
          Documentos
        </TabNavItem>
        <TabNavItem
          type="button"
          isActive={activeTab === 'atividade'}
          onClick={() => setActiveTab('atividade')}
        >
          Atividade
        </TabNavItem>
      </TabsNavContainer>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {/* Conteúdo da Modal de acordo com a Aba selecionada */}
      <ModalBodyContent>
        {activeTab === 'dados' && (
          <>
            {/* 1. IDENTIFICAÇÃO */}
            <SectionTitle style={{ marginTop: '0.2rem' }}>Identificação</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>
                  NOME COMPLETO
                  <Info
                    size={14}
                    style={{ marginLeft: 5, verticalAlign: 'middle' }}
                    color={!formData.name ? 'red' : 'gray'}
                  />
                </LabelCreate>
                <InputField
                  placeholder="Digite o nome completo"
                  type="text"
                  value={formData.name}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('name', e.target.value)
                  }
                />
                {!formData.name && click && <WarningText>O campo Nome completo é obrigatório!</WarningText>}
              </InputDiv>
              <InputDiv>
                <LabelCreate>
                  E-MAIL
                  <Info
                    size={14}
                    style={{ marginLeft: 5, verticalAlign: 'middle' }}
                    color={!formData.email ? 'red' : 'gray'}
                  />
                </LabelCreate>
                <InputField
                  placeholder="Digite o email"
                  type="email"
                  value={formData.email}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('email', e.target.value)
                  }
                />
                {!formData.email && click && <WarningText>O campo E-mail é obrigatório!</WarningText>}
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>SENHA</LabelCreate>
                <InputField
                  placeholder="••••••••"
                  type="password"
                  value=""
                  disabled
                  autoComplete="new-password"
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>PERFIL DE ACESSO (OPCIONAL)</LabelCreate>
                <SelectField
                  value={formData.profileId || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleInputChange('profileId', e.target.value)
                  }
                >
                  <option value="">Sem perfil</option>
                  {profiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </SelectField>
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>CPF</LabelCreate>
                <InputField
                  placeholder="000.000.000-00"
                  type="text"
                  value={formData.cpf || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('cpf', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>RG</LabelCreate>
                <InputField
                  placeholder="00.000.000-0"
                  type="text"
                  value={formData.rg || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('rg', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>CNPJ (SE PJ)</LabelCreate>
                <InputField
                  placeholder="00.000.000/0000-00"
                  type="text"
                  value={formData.cnpj || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('cnpj', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>REGIME</LabelCreate>
                <SelectField
                  value={formData.regime || 'CLT'}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleInputChange('regime', e.target.value)
                  }
                >
                  <option value="CLT">CLT</option>
                  <option value="PJ">PJ</option>
                </SelectField>
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>DATA DE NASCIMENTO</LabelCreate>
                <InputField
                  placeholder="dd/mm/aaaa"
                  type="text"
                  value={formData.birthDate || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('birthDate', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>ESTADO CIVIL</LabelCreate>
                <SelectField
                  value={formData.maritalStatus || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleInputChange('maritalStatus', e.target.value)
                  }
                >
                  <option value="">—</option>
                  <option value="Solteiro(a)">Solteiro(a)</option>
                  <option value="Casado(a)">Casado(a)</option>
                  <option value="Divorciado(a)">Divorciado(a)</option>
                  <option value="Viúvo(a)">Viúvo(a)</option>
                  <option value="União Estável">União Estável</option>
                </SelectField>
              </InputDiv>
            </Grid2Cols>

            {/* 2. DOCUMENTOS TRABALHISTAS (CLT) - RENDERIZAÇÃO CONDICIONAL */}
            {formData.regime !== 'PJ' && (
              <>
                <SectionTitle>Documentos Trabalhistas (CLT)</SectionTitle>
                <Grid2Cols>
                  <InputDiv>
                    <LabelCreate>PIS</LabelCreate>
                    <InputField
                      placeholder="000.00000.00-0"
                      type="text"
                      value={formData.pis || ''}
                      disabled={isInactive}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange('pis', e.target.value)
                      }
                    />
                  </InputDiv>
                  <InputDiv>
                    <LabelCreate>CTPS</LabelCreate>
                    <InputField
                      placeholder="nº da carteira"
                      type="text"
                      value={formData.ctps || ''}
                      disabled={isInactive}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange('ctps', e.target.value)
                      }
                    />
                  </InputDiv>
                </Grid2Cols>

                <Grid2Cols style={{ marginTop: '0.4rem' }}>
                  <InputDiv>
                    <LabelCreate>SÉRIE</LabelCreate>
                    <InputField
                      placeholder="série"
                      type="text"
                      value={formData.ctpsSerie || ''}
                      disabled={isInactive}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange('ctpsSerie', e.target.value)
                      }
                    />
                  </InputDiv>
                  <InputDiv>
                    <LabelCreate>UF DA CTPS</LabelCreate>
                    <InputField
                      placeholder="SP"
                      type="text"
                      value={formData.ctpsUf || ''}
                      disabled={isInactive}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange('ctpsUf', e.target.value)
                      }
                    />
                  </InputDiv>
                </Grid2Cols>

                <Grid2Cols style={{ marginTop: '0.4rem' }}>
                  <InputDiv>
                    <LabelCreate>DATA DE ADMISSÃO</LabelCreate>
                    <InputField
                      placeholder="dd/mm/aaaa"
                      type="text"
                      value={formData.admissionDate || ''}
                      disabled={isInactive}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange('admissionDate', e.target.value)
                      }
                    />
                  </InputDiv>
                  <InputDiv>
                    <LabelCreate>MATRÍCULA ESOCIAL</LabelCreate>
                    <InputField
                      placeholder="ES-000000"
                      type="text"
                      value={formData.esocialRegistry || ''}
                      disabled={isInactive}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange('esocialRegistry', e.target.value)
                      }
                    />
                  </InputDiv>
                </Grid2Cols>
              </>
            )}

            {/* 3. CONTATO */}
            <SectionTitle>Contato</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>TELEFONE / WHATSAPP</LabelCreate>
                <InputField
                  placeholder="(00) 00000-0000"
                  type="text"
                  value={formData.phone || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('phone', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>CONTATO DE EMERGÊNCIA (NOME)</LabelCreate>
                <InputField
                  placeholder="quem avisar"
                  type="text"
                  value={formData.emergencyContactName || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('emergencyContactName', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>CONTATO DE EMERGÊNCIA (TELEFONE)</LabelCreate>
                <InputField
                  placeholder="(00) 00000-0000"
                  type="text"
                  value={formData.emergencyContactPhone || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('emergencyContactPhone', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>PARENTESCO</LabelCreate>
                <InputField
                  placeholder="ex.: cônjuge, mãe, irmão"
                  type="text"
                  value={formData.kinship || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('kinship', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            {/* 4. ENDEREÇO */}
            <SectionTitle>Endereço</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>ENDEREÇO</LabelCreate>
                <InputField
                  placeholder="rua / avenida"
                  type="text"
                  value={formData.street || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('street', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>NÚMERO</LabelCreate>
                <InputField
                  placeholder="nº"
                  type="text"
                  value={formData.number || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('number', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>BAIRRO</LabelCreate>
                <InputField
                  placeholder="Bairro"
                  type="text"
                  value={formData.neighborhood || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('neighborhood', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>CIDADE / UF</LabelCreate>
                <InputField
                  placeholder="Cidade / UF"
                  type="text"
                  value={formData.cityUf || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('cityUf', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>CEP</LabelCreate>
                <InputField
                  placeholder="00000-000"
                  type="text"
                  value={formData.cep || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('cep', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            {/* 5. DADOS BANCÁRIOS */}
            <SectionTitle>Dados Bancários</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>CHAVE PIX</LabelCreate>
                <InputField
                  placeholder="CPF, e-mail, telefone ou aleatória"
                  type="text"
                  value={formData.pixKey || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('pixKey', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>BANCO</LabelCreate>
                <InputField
                  placeholder="Banco"
                  type="text"
                  value={formData.bank || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('bank', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>AGÊNCIA</LabelCreate>
                <InputField
                  placeholder="Agência"
                  type="text"
                  value={formData.agency || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('agency', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>CONTA</LabelCreate>
                <InputField
                  placeholder="Conta"
                  type="text"
                  value={formData.account || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('account', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            {/* 6. DEPENDENTES E PENSÃO */}
            <SectionTitle>Dependentes e Pensão</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>FILHOS (DEPENDENTES)</LabelCreate>
                <InputField
                  placeholder="ex.: 2 (Ana, 2019 · Pedro, 2022)"
                  type="text"
                  value={formData.dependentsInfo || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('dependentsInfo', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>PENSÃO ALIMENTÍCIA</LabelCreate>
                <InputField
                  placeholder="valor ou % descontado em folha"
                  type="text"
                  value={formData.alimony || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('alimony', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            {/* 7. UNIFORME E EPI (NUMERAÇÃO) */}
            <SectionTitle>Uniforme e EPI (Numeração)</SectionTitle>
            <Grid4Cols>
              <InputDiv>
                <LabelCreate>CAMISETA</LabelCreate>
                <InputField
                  placeholder="P/M/G"
                  type="text"
                  value={formData.tshirtSize || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('tshirtSize', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>CAMISA</LabelCreate>
                <InputField
                  placeholder="P/M/G"
                  type="text"
                  value={formData.shirtSize || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('shirtSize', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>CALÇA</LabelCreate>
                <InputField
                  placeholder="nº"
                  type="text"
                  value={formData.pantsSize || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('pantsSize', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>BOTA</LabelCreate>
                <InputField
                  placeholder="nº"
                  type="text"
                  value={formData.bootSize || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('bootSize', e.target.value)
                  }
                />
              </InputDiv>
            </Grid4Cols>

            {/* 8. FÉRIAS E REAJUSTE ANUAL */}
            <SectionTitle>Férias e Reajuste Anual</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>PERÍODO AQUISITIVO (INÍCIO)</LabelCreate>
                <InputField
                  placeholder="mm/aaaa"
                  type="text"
                  value={formData.vacationStartMonthYear || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('vacationStartMonthYear', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>PERÍODO AQUISITIVO (FIM)</LabelCreate>
                <InputField
                  placeholder="mm/aaaa"
                  type="text"
                  value={formData.vacationEndMonthYear || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('vacationEndMonthYear', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <Grid2Cols style={{ marginTop: '0.4rem' }}>
              <InputDiv>
                <LabelCreate>DATA DE PAGAMENTO DAS FÉRIAS</LabelCreate>
                <InputField
                  placeholder="dd/mm/aaaa"
                  type="text"
                  value={formData.vacationPayDate || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('vacationPayDate', e.target.value)
                  }
                />
              </InputDiv>
              <InputDiv>
                <LabelCreate>DISSÍDIO / REAJUSTE ANUAL (REGRA GLOBAL)</LabelCreate>
                <InputField
                  placeholder="Março (Vestuário SP) · último: 03/2026 (+5,2%)"
                  type="text"
                  value={formData.annualAdjustmentRule || ''}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('annualAdjustmentRule', e.target.value)
                  }
                />
              </InputDiv>
            </Grid2Cols>

            <CalloutBox>
              <span>📋 O controle de férias gera o contrato/recibo de férias na aba Documentos, e o pagamento entra no contas a pagar na data configurada.</span>
            </CalloutBox>

            {/* 9. ALOCAÇÃO FINANCEIRA (BU · CENTRO DE CUSTO) */}
            <SectionTitle>Alocação Financeira (BU · Centro de Custo)</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>BUSINESS UNIT (BU)</LabelCreate>
                <SelectField
                  value={formData.businessUnit || 'Dustre B2B'}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleInputChange('businessUnit', e.target.value)
                  }
                >
                  <option value="Dustre B2B">Dustre B2B</option>
                  <option value="Dustre Varejo">Dustre Varejo</option>
                  <option value="Matriz">Matriz</option>
                </SelectField>
              </InputDiv>
              <InputDiv>
                <LabelCreate>CENTRO DE CUSTO</LabelCreate>
                <SelectField
                  value={formData.costCenter || 'Comercial'}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleInputChange('costCenter', e.target.value)
                  }
                >
                  <option value="Comercial">Comercial</option>
                  <option value="Operacional">Operacional</option>
                  <option value="Administrativo">Administrativo</option>
                  <option value="TI / Tecnologia">TI / Tecnologia</option>
                  <option value="Financeiro">Financeiro</option>
                </SelectField>
              </InputDiv>
            </Grid2Cols>

            <CalloutBox>
              <span>💰 Todo lançamento financeiro do colaborador (salário, comissão, benefícios, ajuda de custo) é classificado nesta BU e centro de custo — é o que a Conciliação valida.</span>
            </CalloutBox>

            {/* 10. COMPOSIÇÃO VIA REGRAS (SOMENTE LEITURA) */}
            <SectionTitle>Composição via Regras (Somente Leitura)</SectionTitle>
            <CompositionBox>
              <CompositionRow>
                <CompositionLabel>Salário base (Cargo)</CompositionLabel>
                <CompositionValue>{formData.baseSalary || '— sem cargo vinculado'}</CompositionValue>
              </CompositionRow>
              <CompositionRow>
                <CompositionLabel>Bônus (Cargo)</CompositionLabel>
                <CompositionValue>{formData.bonus || '—'}</CompositionValue>
              </CompositionRow>
              <CompositionRow>
                <CompositionLabel>Comissão (Regras)</CompositionLabel>
                <CompositionValue>{formData.commission || 'Carteira padrão (3%)'}</CompositionValue>
              </CompositionRow>
              <CompositionRow>
                <CompositionLabel>Adicionais (Regras)</CompositionLabel>
                <CompositionValue>{formData.additionals || '—'}</CompositionValue>
              </CompositionRow>
              <CompositionRow>
                <CompositionLabel>Benefícios (Perfil VENDEDOR)</CompositionLabel>
                <CompositionValue>{formData.benefits || '—'}</CompositionValue>
              </CompositionRow>
            </CompositionBox>
            <span style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.4rem', display: 'block', fontFamily: 'Inter, sans-serif' }}>
              ⚙️ Cargo, comissão e benefícios são configurados em Regras e vinculados à pessoa — edite lá.
            </span>

            {/* 11. ACESSO */}
            <SectionTitle>Acesso</SectionTitle>
            <Grid2Cols>
              <InputDiv>
                <LabelCreate>STATUS</LabelCreate>
                <SelectField
                  value={formData.status || 'EM RISCO'}
                  disabled={isInactive}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleInputChange('status', e.target.value)
                  }
                >
                  <option value="EM RISCO">EM RISCO</option>
                  <option value="ATIVO">ATIVO</option>
                  <option value="INATIVO">INATIVO</option>
                  <option value="PENDENTE">PENDENTE</option>
                </SelectField>
              </InputDiv>
              <InputDiv>
                <LabelCreate>ÚLTIMO LOGIN</LabelCreate>
                <InputField
                  placeholder="12/06"
                  type="text"
                  value={formData.lastLogin || ''}
                  disabled
                />
              </InputDiv>
            </Grid2Cols>

            <ActionOutlineButton type="button">
              ✉️ Reenviar convite de acesso
            </ActionOutlineButton>

            {/* 12. HABILIDADES (TAGS) */}
            <SectionTitle>Habilidades (Tags)</SectionTitle>
            <InputDiv style={{ marginBottom: '1rem' }}>
              <InfiniteSelect
                key={skillInputKey}
                mode="crud"
                label=""
                placeholder="ex.: Empilhadeira, Caminhão, Excel, SolidW"
                endpointConfig={{
                  baseUrl: process.env.REACT_APP_GATEWAY_BASE_URL,
                  listPath: '/backend/skills',
                  createPath: '/backend/skills',
                  searchParam: 'search',
                  limitParam: 'take',
                  offsetParam: 'skip',
                }}
                allowCreate={true}
                allowDelete={false}
                showCreateOnlyIfNotExists={true}
                showSelectedBelow={false}
                value={null}
                disabled={isInactive}
                onSelect={(skill) => {
                  if (skill && !selectedSkills.find((s) => s.id === skill.id)) {
                    setSelectedSkills((prev) => [...prev, { id: skill.id, name: skill.name }])
                  }
                  setSkillInputKey((k) => k + 1)
                }}
              />
              {selectedSkills.length > 0 && (
                <SkillChipsWrap>
                  {selectedSkills.map((skill) => (
                    <SkillChip key={skill.id}>
                      {skill.name}
                      <SkillChipRemoveButton
                        type="button"
                        onClick={() =>
                          setSelectedSkills((prev) => prev.filter((s) => s.id !== skill.id))
                        }
                        aria-label={`Remover ${skill.name}`}
                      >
                        ×
                      </SkillChipRemoveButton>
                    </SkillChip>
                  ))}
                </SkillChipsWrap>
              )}
            </InputDiv>
          </>
        )}

        {activeTab === 'vinculos' && (
          <div style={{ padding: '2rem 0', textAlign: 'center', color: '#6B7280' }}>
            <p>Vínculos e regras do colaborador (Em breve)</p>
          </div>
        )}

        {activeTab === 'documentos' && (
          <UserDocumentsTab
            attachments={attachments}
            setAttachments={setAttachments}
            userRegime={formData.regime}
            disabled={isInactive}
          />
        )}

        {activeTab === 'atividade' && (
          <div style={{ padding: '2rem 0', textAlign: 'center', color: '#6B7280' }}>
            <p>Histórico de atividades do colaborador (Em breve)</p>
          </div>
        )}
      </ModalBodyContent>

      {/* Rodapé Padronizado Ordeno */}
      <FooterContainer>
        {isInactive ? (
          <ActivateButtonDiv
            type="button"
            onClick={() => setStatusConfirmAction('activate')}
          >
            Ativar usuário
          </ActivateButtonDiv>
        ) : (
          <DangerOutlineButton
            type="button"
            onClick={() => setStatusConfirmAction('deactivate')}
          >
            Inativar usuário
          </DangerOutlineButton>
        )}

        <RightFooterActions>
          {!isInactive && !isValid && click && (
            <TipText>Preencha todos os campos obrigatórios corretamente</TipText>
          )}

          <SecondaryButton
            type="button"
            onClick={handleCloseModal}
          >
            Fechar
          </SecondaryButton>

          <PrimaryButton
            type="button"
            disabled={isInactive}
            onClick={isInactive ? undefined : handleSave}
          >
            <Save width={16} height={16} />
            Salvar alterações
          </PrimaryButton>
        </RightFooterActions>
      </FooterContainer>

      <ConfirmationModal
        isOpen={statusConfirmAction !== null}
        title={
          statusConfirmAction === 'activate'
            ? 'Ativar usuário'
            : 'Desativar usuário'
        }
        message={
          statusConfirmAction === 'activate' ? (
            <p style={{ margin: 0 }}>
              O usuário voltará a ter acesso conforme o perfil de acesso definido.
            </p>
          ) : (
            <p style={{ margin: 0 }}>
              Isso poderá ser revertido, mas o usuário não terá acesso até então.
            </p>
          )
        }
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setStatusConfirmAction(null),
            variant: 'outline',
          },
          statusConfirmAction === 'activate'
            ? {
                label: 'Ativar',
                onClick: handleConfirmStatusChange,
                variant: 'primary' as const,
              }
            : {
                label: 'Desativar',
                onClick: handleConfirmStatusChange,
                variant: 'danger' as const,
              },
        ]}
      />
    </Container>
  )
}

export default EditAccessUserModal
